import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField,
  MenuItem, Typography, Box, Paper, Divider, Grid, Stack, Tabs, Tab,
  Card, CardContent, Avatar, Chip, Alert, CircularProgress, AlertTitle,
  IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import {
  Business, Receipt, AttachMoney, Description, AccountBalance, Warning,
  CheckCircle, LocationOn, Save, Refresh, Info, ExpandMore, Person, Assignment,
  Edit, Storefront,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import client from "../../../API";
import {
  dateToDatetimeLocal, invoiceStatuses, parseLocalFloat, parseLocalInt,
} from "../../../Utils/constants";

/* ============================================================ CONSTANTS === */

const BLITZ_DEFAULTS = {
  company_name:  'Pangea Omninational Corporation',
  address_line1: '8724 Cavell Lane',
  city:          'Houston',
  state:         'TX',
  zip_code:      '77055',
  country:       'USA',
  email:         'win@useblitz.co',
};

const FUNDING_SOURCES = [
  { value: 'internal_capital', label: 'Internal Capital' },
  { value: 'external_partner', label: 'External Partner' },
];
const PAYMENT_METHODS = [
  { value: 'stripe',  label: 'Stripe' },
  { value: 'ach',     label: 'ACH Transfer' },
  { value: 'wire',    label: 'Wire Transfer' },
  { value: 'check',   label: 'Check' },
  { value: 'manual',  label: 'Manual' },
  { value: 'unknown', label: 'Other' },
];
const INVOICE_TYPE_CONFIG = {
  blitzpay: { label:'BlitzPay', color:'#667eea', description:'Invoice factoring for immediate payment' },
  project:  { label:'Project',  color:'#3b82f6', description:'Standard project invoice',  billingAddressLabel:'Bill To Address', billingAddressHelper:"Client's billing address for invoice delivery" },
  client:   { label:'Client',   color:'#10b981', description:'Client service invoice',    billingAddressLabel:'Bill To Address', billingAddressHelper:"Client's billing address for invoice delivery" },
  creator:  { label:'Creator',  color:'#f59e0b', description:'Creator payment invoice',   billingAddressLabel:'Bill To Address', billingAddressHelper:"Client's billing address for payment" },
  partial:  { label:'Partial',  color:'#8b5cf6', description:'Partial payment invoice',   billingAddressLabel:'Bill To Address', billingAddressHelper:"Client's billing address for invoice delivery" },
};

const getAllowedStatuses = (cur) => ({
  pending:   ['pending','approved','cancelled'],
  approved:  ['approved','paid','funded'],
  paid:      ['paid'],
  cancelled: ['cancelled','pending'],
  funded:    ['funded','paid'],
}[cur] || ['pending','approved','paid','cancelled','funded']);

const getStatusHelperText = (cur, sel) => {
  if (!cur) return '';
  const ok = getAllowedStatuses(cur);
  if (sel && !ok.includes(sel)) {
    if (cur==='pending'  && (sel==='paid'||sel==='funded')) return '⚠️ Invoice must be approved before it can be paid or funded';
    if (cur==='approved' && sel==='pending')  return '⚠️ Cannot revert an approved invoice to pending';
    if (cur==='paid')                          return '⚠️ Paid invoices cannot be changed to another status';
    if (cur==='funded'   && sel==='approved')  return '⚠️ Cannot revert a funded invoice to approved';
    return '⚠️ Invalid status transition';
  }
  if (cur==='pending')  return 'Pending invoices can be approved or cancelled';
  if (cur==='approved') return 'Approved invoices can be paid or funded';
  if (cur==='funded')   return 'Funded invoices can be marked as paid — this will automatically restore spending power';
  return '';
};

const stripEmptyValues = (obj = {}) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== null && value !== undefined && value !== "")
  );

const EMPTY_PROJECT_BANK_OVERRIDE = {
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  routingNumber: "",
  swift: "",
  beneficiaryAddress: "",
};

/* ============================================================== HOOKS === */

const useInvoiceCore = (inv) => {
  const [s, set] = useState({ poNumber:"", externalPO:"", amountDue:"0.00", feePercentage:"0", status:"", originalStatus:"", paymentTerm:"30", datePaid:null, fundedBy:"", fundedDate:null });
  useEffect(() => {
    if (!inv) return;
    set({
      poNumber: inv.po_number||"", externalPO: inv.external_po_number||"",
      amountDue: inv.amount_due?.toFixed(2)||"0.00", feePercentage: inv.fee_percentage != null ? String(inv.fee_percentage) : "0",
      status: inv.status||"", originalStatus: inv.status||"", paymentTerm: inv.payment_term?.toString()||"30",
      datePaid:   inv.date_paid   ? new Date(inv.date_paid)   : null,
      fundedBy:   inv.funded_by||"",
      fundedDate: inv.funded_date ? new Date(inv.funded_date) : null,
    });
  }, [inv]);
  return [s, set];
};

const useClientInfo = (inv) => {
  const [s, set] = useState({ billedToName:"", billedToEmail:"", issuingCompany:"", amountPaid:"" });
  useEffect(() => {
    if (!inv) return;
    set({ billedToName:inv.client_name||"", billedToEmail:inv.client_email||"", issuingCompany:inv.custom_details?.issuing_company||"", amountPaid:inv.custom_details?.amount_paid?.toFixed(2)||"" });
  }, [inv]);
  return [s, set];
};

const useBillingAddress = (inv) => {
  const [s, set] = useState({ companyName:"", address1:"", address2:"", city:"", state:"", zip:"", country:"USA" });
  useEffect(() => {
    if (!inv) return;
    const a = inv.custom_details?.billing_address || {};
    set({ companyName:a.company_name||"", address1:a.address_line1||"", address2:a.address_line2||"", city:a.city||"", state:a.state||"", zip:a.zip_code||"", country:a.country||"USA" });
  }, [inv]);
  return [s, set];
};

const useProjectBankOverride = (inv) => {
  const [s, set] = useState(EMPTY_PROJECT_BANK_OVERRIDE);

  useEffect(() => {
    if (!inv) return;
    const customDetails = inv.custom_details || {};
    const mergedBank = {
      ...(customDetails?.billed_to_info?.bank_details || {}),
      ...(customDetails?.additional_info?.bank_details || {}),
    };
    set({
      ...EMPTY_PROJECT_BANK_OVERRIDE,
      bankName: mergedBank.bank_name || "",
      accountHolder: mergedBank.account_holder || "",
      accountNumber: mergedBank.account_number || "",
      routingNumber: mergedBank.routing_number || "",
      swift: mergedBank.swift_code || mergedBank.swift || "",
      beneficiaryAddress: mergedBank.beneficiary_address || "",
    });
  }, [inv]);

  return [s, set];
};

/**
 * BLITZPAY — "Billed To" = the USER'S OWN company (the recipient of the BlitzPay advance).
 * Shown in the left "Billed To" section of the invoice.
 * Seeded from BlitzPayCompanyProfile -> invoice.custom_details.billed_to_info -> user fields.
 */
const useBlitzPayBilledTo = (inv) => {
  const [s, set] = useState({
    companyName:        "",
    senderDisplayName:  "",
    contactName:        "",
    email:              "",
    phone:              "",
    address1:           "",
    address2:           "",
    city:               "",
    state:              "",
    zip:                "",
    country:            "USA",
    taxId:              "",
    amountPaid:         "",
    bankAccountHolder: "",
    bankAccountName:   "",
    bankName:          "",
    bankAddress:       "",
    accountNumber:     "",
    routingNumber:     "",
    swift:             "",
    paymentInstructions: "",
    termsAndConditions:  "",
    notes:               "",
    logoUrl:             "",
    vendorId:    "",
    erpSystem:   "",
  });

  useEffect(() => {
    if (!inv) return;
    const bt = inv.custom_details?.billed_to_info || {};
    const ai = bt.additional_info || {};
    const bk = bt.bank_details    || {};
    set({
      companyName:       bt.company_name       || inv.client_name  || "",
      senderDisplayName: bt.sender_display_name || "",
      contactName:       bt.contact_name        || "",
      email:             bt.email               || inv.client_email || "",
      phone:             bt.phone               || "",
      address1:          bt.address_line1       || "",
      address2:          bt.address_line2       || "",
      city:              bt.city                || "",
      state:             bt.state               || "",
      zip:               bt.zip_code            || "",
      country:           bt.country             || "USA",
      taxId:             bt.tax_id              || "",
      amountPaid:        inv.custom_details?.amount_paid?.toFixed(2) || "",
      bankAccountHolder: bk.account_holder      || "",
      bankAccountName:   bk.account_name        || "",
      bankName:          bk.bank_name           || "",
      bankAddress:       bk.bank_address        || "",
      accountNumber:     bk.account_number      || "",
      routingNumber:     bk.routing_number      || "",
      swift:             bk.swift               || "",
      paymentInstructions: ai.payment_instructions || "",
      termsAndConditions:  ai.terms_and_conditions || "",
      notes:               ai.notes               || "",
      logoUrl:             ai.logo_url            || bt.logo_url || "",
      vendorId:  bt.vendor_id  || "",
      erpSystem: bt.erp_system || "",
    });
  }, [inv]);

  return [s, set];
};

/**
 * BLITZPAY — "Billed From" = the invoice SENDER / factor.
 * This is what appears in the TOP-RIGHT of the invoice.
 * Seeded from custom_details.billed_from_info with BLITZ_DEFAULTS as fallback.
 * Saved back to custom_details.billed_from_info on submit.
 *
 * Key distinction: billed_from_info is PER-INVOICE only (not tied to the user profile),
 * since Blitz may register under different vendor names in different client AP systems.
 */
const useBilledFrom = (inv) => {
  const [s, set] = useState({
    companyName:       BLITZ_DEFAULTS.company_name,
    senderDisplayName: '',
    address1:          BLITZ_DEFAULTS.address_line1,
    address2:          '',
    city:              BLITZ_DEFAULTS.city,
    state:             BLITZ_DEFAULTS.state,
    zip:               BLITZ_DEFAULTS.zip_code,
    country:           BLITZ_DEFAULTS.country,
    email:             BLITZ_DEFAULTS.email,
    phone:             '',
    taxId:             '',
    logoUrl:           '',
  });

  useEffect(() => {
    if (!inv) return;
    const bf = inv.custom_details?.billed_from_info || {};
    set({
      companyName:       bf.company_name        || BLITZ_DEFAULTS.company_name,
      senderDisplayName: bf.sender_display_name || '',
      address1:          bf.address_line1       || BLITZ_DEFAULTS.address_line1,
      address2:          bf.address_line2       || '',
      city:              bf.city                || BLITZ_DEFAULTS.city,
      state:             bf.state               || BLITZ_DEFAULTS.state,
      zip:               bf.zip_code            || BLITZ_DEFAULTS.zip_code,
      country:           bf.country             || BLITZ_DEFAULTS.country,
      email:             bf.email               || BLITZ_DEFAULTS.email,
      phone:             bf.phone               || '',
      taxId:             bf.tax_id              || '',
      logoUrl:           bf.logo_url            || '',
    });
  }, [inv]);

  return [s, set];
};

const applyProfileToBilledTo = (prev, profile, user) => ({
  companyName:       prev.companyName       || profile?.company_name         || user?.company_name  || "",
  senderDisplayName: prev.senderDisplayName || profile?.sender_display_name  || "",
  contactName:       prev.contactName       || "",
  email:             prev.email             || profile?.contact?.email        || user?.email         || "",
  phone:             prev.phone             || profile?.contact?.phone        || user?.phone_number  || "",
  address1:          prev.address1          || profile?.address?.line1        || "",
  address2:          prev.address2          || profile?.address?.line2        || "",
  city:              prev.city              || profile?.address?.city         || "",
  state:             prev.state             || profile?.address?.state        || "",
  zip:               prev.zip               || profile?.address?.zip_code     || "",
  country:           prev.country           || profile?.address?.country      || "USA",
  taxId:             prev.taxId             || profile?.tax_id                || "",
  amountPaid:        prev.amountPaid,
  bankAccountHolder: prev.bankAccountHolder || profile?.bank_details?.account_holder || "",
  bankAccountName:   prev.bankAccountName   || profile?.bank_details?.account_name   || "",
  bankName:          prev.bankName          || profile?.bank_details?.bank_name      || "",
  bankAddress:       prev.bankAddress       || profile?.bank_details?.bank_address   || "",
  accountNumber:     prev.accountNumber     || profile?.bank_details?.account_number || "",
  routingNumber:     prev.routingNumber     || profile?.bank_details?.routing_number || "",
  swift:             prev.swift             || profile?.bank_details?.swift_code     || "",
  paymentInstructions: prev.paymentInstructions || profile?.terms?.payment_instructions || "",
  termsAndConditions:  prev.termsAndConditions  || profile?.terms?.default_terms        || "",
  notes:               prev.notes               || profile?.terms?.invoice_notes        || "",
  logoUrl:             prev.logoUrl             || profile?.logo_url                    || "",
  vendorId:   prev.vendorId  || profile?.vendor_info?.vendor_id  || "",
  erpSystem:  prev.erpSystem || profile?.vendor_info?.erp_system || "",
});

const useRepaymentState = (inv) => {
  const [s, set] = useState({ isEligible:false, validation:null, isValidating:false, externalTransactionId:"", paymentMethod:"", notes:"" });
  useEffect(() => {
    const check = async () => {
      if (!inv?.id) return;
      const quick = inv.invoice_type==='blitzpay' && inv.status==='funded' && inv.spending_ledger_id!=null;
      if (!quick) { set(p=>({...p,isEligible:false,validation:null})); return; }
      set(p=>({...p,isValidating:true}));
      try {
        const v = await client.blitzpayRepayments.validateRepayment(inv.id);
        set(p=>({...p,isEligible:v.eligible,validation:v,isValidating:false}));
      } catch { set(p=>({...p,isEligible:false,validation:null,isValidating:false})); }
    };
    check();
  }, [inv]);
  return [s, set];
};

/* ========================================================= MAIN DIALOG === */

const InvoiceDialog = ({ open, onClose, invoiceInfo = undefined }) => {
  const [activeTab, setActiveTab] = useState(0);

  const invoiceType = invoiceInfo?.invoice_type || 'project';
  const typeConfig  = INVOICE_TYPE_CONFIG[invoiceType] || INVOICE_TYPE_CONFIG.project;
  const isBlitzPay  = invoiceType === 'blitzpay';
  const userId      = invoiceInfo?.user_id || invoiceInfo?.client_id;

  const [coreState,      setCoreState]      = useInvoiceCore(invoiceInfo);
  const [clientInfo,     setClientInfo]     = useClientInfo(invoiceInfo);
  const [billingAddress, setBillingAddress] = useBillingAddress(invoiceInfo);
  const [projectBank,    setProjectBank]    = useProjectBankOverride(invoiceInfo);
  const [billedTo,       setBilledTo]       = useBlitzPayBilledTo(invoiceInfo);
  const [billedFrom,     setBilledFrom]     = useBilledFrom(invoiceInfo);
  const [repaymentState, setRepaymentState] = useRepaymentState(invoiceInfo);
  const [profileReady,   setProfileReady]   = useState(false);

  const [enterpriseClient, setEnterpriseClient] = useState({ name:"", email:"" });
  useEffect(() => {
    if (!invoiceInfo) return;
    setEnterpriseClient({ name: invoiceInfo.client_name||"", email: invoiceInfo.client_email||"" });
  }, [invoiceInfo]);

  const { data:profileResp, isLoading:profileLoading, refetch:refetchProfile } = useQuery(
    ['blitzpay-profile-dialog', userId],
    () => client.blitzpayProfiles.get(userId),
    { enabled: !!userId && isBlitzPay && open, staleTime: 2*60*1000 }
  );

  useEffect(() => {
    if (profileLoading || profileReady || !isBlitzPay) return;
    const profile = profileResp?.profile || null;
    const user    = profileResp?.user    || null;
    setBilledTo(prev => applyProfileToBilledTo(prev, profile, user));
    setProfileReady(true);
  }, [profileLoading, profileResp, profileReady, isBlitzPay]);

  useEffect(() => { if (open) setProfileReady(false); }, [open, invoiceInfo?.id]);

  const loadedProfile = profileResp?.profile || null;

  const invoiceMutation   = useMutation(p => client.invoices.editAdmin(p));
  const profileMutation   = useMutation(p => client.blitzpayProfiles.update(userId, p));
  const repaymentMutation = useMutation(
    d => client.blitzpayRepayments.processRepayment(d),
    { onSuccess: d => toast.success(`Repayment processed! Restored $${d.amount_restored.toFixed(2)}`, { autoClose:5000 }) }
  );

  const isLoading        = invoiceMutation.isLoading || repaymentMutation.isLoading;
  const allowedStatuses  = getAllowedStatuses(coreState.originalStatus);
  const isValidStatus    = !coreState.status || allowedStatuses.includes(coreState.status);
  const statusHelper     = getStatusHelperText(coreState.originalStatus, coreState.status);
  const isChangingToPaid = coreState.originalStatus === 'funded' && coreState.status === 'paid';

  /* ── handleSubmit ─────────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!isValidStatus) { toast.error(`Invalid status transition. ${statusHelper}`); return; }
    try {
      const payload = {
        id: invoiceInfo.id,
        po_number:          coreState.poNumber.trim(),
        amount_due:         parseLocalFloat(coreState.amountDue),
        fee_percentage:     parseLocalFloat(coreState.feePercentage),
        payment_term:       parseLocalInt(coreState.paymentTerm),
        funded_by:          coreState.fundedBy.trim() || undefined,
      };
      if (coreState.status?.trim()) payload.status      = coreState.status.trim();
      if (coreState.datePaid)       payload.date_paid   = coreState.datePaid.toISOString();
      if (coreState.fundedDate)     payload.funded_date = coreState.fundedDate.toISOString();

      if (isBlitzPay) {
        payload.client_name  = enterpriseClient.name.trim();
        payload.client_email = enterpriseClient.email.trim();

        payload.custom_details = {
          amount_paid: billedTo.amountPaid ? parseLocalFloat(billedTo.amountPaid) : undefined,

          // ── User's company (the BlitzPay recipient) ────────────────────
          billed_to_info: {
            company_name:        billedTo.companyName.trim()       || undefined,
            sender_display_name: billedTo.senderDisplayName.trim() || undefined,
            contact_name:        billedTo.contactName.trim()       || undefined,
            email:               billedTo.email.trim()             || undefined,
            phone:               billedTo.phone.trim()             || undefined,
            address_line1:       billedTo.address1.trim()          || undefined,
            address_line2:       billedTo.address2.trim()          || undefined,
            city:                billedTo.city.trim()              || undefined,
            state:               billedTo.state.trim()             || undefined,
            zip_code:            billedTo.zip.trim()               || undefined,
            country:             billedTo.country.trim()           || undefined,
            tax_id:              billedTo.taxId.trim()             || undefined,
            logo_url:            billedTo.logoUrl.trim()           || undefined,
            vendor_id:           billedTo.vendorId.trim()          || undefined,
            erp_system:          billedTo.erpSystem.trim()         || undefined,
            bank_details: {
              account_holder: billedTo.bankAccountHolder.trim() || undefined,
              account_name:   billedTo.bankAccountName.trim()   || undefined,
              bank_name:      billedTo.bankName.trim()          || undefined,
              bank_address:   billedTo.bankAddress.trim()       || undefined,
              account_number: billedTo.accountNumber.trim()     || undefined,
              routing_number: billedTo.routingNumber.trim()     || undefined,
              swift:          billedTo.swift.trim()             || undefined,
            },
            additional_info: {
              payment_instructions: billedTo.paymentInstructions.trim() || undefined,
              terms_and_conditions: billedTo.termsAndConditions.trim()  || undefined,
              notes:                billedTo.notes.trim()                || undefined,
            },
          },

          // ── Invoice Sender top-right — editable Blitz/factor identity ──
          // _confirmed: true marks this as intentionally set via the admin dialog.
          // BlitzPayInvoiceDesign checks this flag before trusting address/identity
          // fields — without it, legacy corrupt records are ignored and BLITZ_DEFAULTS
          // are used instead.
          billed_from_info: {
            _confirmed:          true,
            company_name:        billedFrom.companyName.trim()       || BLITZ_DEFAULTS.company_name,
            sender_display_name: billedFrom.senderDisplayName.trim() || undefined,
            address_line1:       billedFrom.address1.trim()          || BLITZ_DEFAULTS.address_line1,
            address_line2:       billedFrom.address2.trim()          || undefined,
            city:                billedFrom.city.trim()              || BLITZ_DEFAULTS.city,
            state:               billedFrom.state.trim()             || BLITZ_DEFAULTS.state,
            zip_code:            billedFrom.zip.trim()               || BLITZ_DEFAULTS.zip_code,
            country:             billedFrom.country.trim()           || BLITZ_DEFAULTS.country,
            email:               billedFrom.email.trim()             || BLITZ_DEFAULTS.email,
            phone:               billedFrom.phone.trim()             || undefined,
            tax_id:              billedFrom.taxId.trim()             || undefined,
            logo_url:            billedFrom.logoUrl.trim()           || undefined,
          },
        };
      } else {
        payload.client_name  = clientInfo.billedToName.trim();
        payload.client_email = clientInfo.billedToEmail.trim();
        payload.custom_details = {
          ...(invoiceInfo.custom_details || {}),
          issuing_company: clientInfo.issuingCompany.trim() || undefined,
          amount_paid:     clientInfo.amountPaid ? parseLocalFloat(clientInfo.amountPaid) : undefined,
          billing_address: {
            company_name:  billingAddress.companyName.trim() || undefined,
            address_line1: billingAddress.address1.trim()    || undefined,
            address_line2: billingAddress.address2.trim()    || undefined,
            city:          billingAddress.city.trim()        || undefined,
            state:         billingAddress.state.trim()       || undefined,
            zip_code:      billingAddress.zip.trim()         || undefined,
            country:       billingAddress.country.trim()     || undefined,
          },
        };

        if (invoiceType === 'project') {
          const cleanedBankDetails = stripEmptyValues({
            bank_name: projectBank.bankName.trim(),
            account_holder: projectBank.accountHolder.trim(),
            account_number: projectBank.accountNumber.trim(),
            routing_number: projectBank.routingNumber.trim(),
            swift_code: projectBank.swift.trim(),
            beneficiary_address: projectBank.beneficiaryAddress.trim(),
          });
          const nextAdditionalInfo = {
            ...(invoiceInfo.custom_details?.additional_info || {}),
          };

          if (Object.keys(cleanedBankDetails).length > 0) {
            nextAdditionalInfo.bank_details = cleanedBankDetails;
          } else {
            delete nextAdditionalInfo.bank_details;
          }

          payload.custom_details.additional_info = nextAdditionalInfo;

          if (payload.custom_details.billed_to_info) {
            payload.custom_details.billed_to_info = {
              ...payload.custom_details.billed_to_info,
            };
            delete payload.custom_details.billed_to_info.bank_details;
          }
        }
      }

      await invoiceMutation.mutateAsync(payload);

      if (isBlitzPay && userId) {
        profileMutation.mutate(
          {
            company_name:         billedTo.companyName.trim()           || undefined,
            sender_display_name:  billedTo.senderDisplayName.trim()     || null,
            address_line1:        billedTo.address1.trim()              || undefined,
            address_line2:        billedTo.address2.trim()              || undefined,
            city:                 billedTo.city.trim()                  || undefined,
            state:                billedTo.state.trim()                 || undefined,
            zip_code:             billedTo.zip.trim()                   || undefined,
            country:              billedTo.country.trim()               || undefined,
            phone:                billedTo.phone.trim()                 || undefined,
            email:                billedTo.email.trim()                 || undefined,
            tax_id:               billedTo.taxId.trim()                 || undefined,
            logo_url:             billedTo.logoUrl.trim()               || undefined,
            primary_vendor_id:    billedTo.vendorId.trim()              || undefined,
            erp_system_name:      billedTo.erpSystem.trim()             || undefined,
            bank_account_holder:  billedTo.bankAccountHolder.trim()     || undefined,
            bank_account_name:    billedTo.bankAccountName.trim()       || undefined,
            bank_name:            billedTo.bankName.trim()              || undefined,
            bank_address:         billedTo.bankAddress.trim()           || undefined,
            account_number:       billedTo.accountNumber.trim()         || undefined,
            routing_number:       billedTo.routingNumber.trim()         || undefined,
            swift_code:           billedTo.swift.trim()                 || undefined,
            payment_instructions: billedTo.paymentInstructions.trim()   || undefined,
            default_terms:        billedTo.termsAndConditions.trim()    || undefined,
            invoice_notes:        billedTo.notes.trim()                 || undefined,
          },
          {
            onError: (e) => {
              console.error('Profile update failed:', e);
              toast.warning('Invoice saved, but profile update failed.', { autoClose:4000 });
            },
          }
        );
      }

      if (isChangingToPaid && repaymentState.isEligible) {
        if (!repaymentState.externalTransactionId.trim()) toast.warning('Transaction ID required');
        else if (!repaymentState.paymentMethod)           toast.warning('Payment method required');
        else {
          try {
            await repaymentMutation.mutateAsync({
              invoice_id:              invoiceInfo.id,
              payment_amount:          parseLocalFloat(coreState.amountDue),
              external_transaction_id: repaymentState.externalTransactionId.trim(),
              payment_method:          repaymentState.paymentMethod,
              notes:                   repaymentState.notes.trim() || undefined,
            });
          } catch { toast.warning('Invoice updated but repayment failed. Process manually.', { autoClose:7000 }); }
        }
      }

      toast.success('Invoice updated successfully!');
      onClose(true);
    } catch (e) {
      console.error(e);
      toast.error(`Failed to save: ${e.message}`);
    }
  };

  const previewInvoiceTotal = parseLocalFloat(coreState.amountDue) || 0;
  const previewFeePercentage = parseLocalFloat(coreState.feePercentage) || 0;
  const previewAmountPaid = useMemo(() => {
    if (!isBlitzPay) return 0;
    const rawAmountPaid = parseLocalFloat(billedTo.amountPaid);
    if (rawAmountPaid == null) return 0;
    return Math.min(Math.max(rawAmountPaid, 0), previewInvoiceTotal);
  }, [isBlitzPay, billedTo.amountPaid, previewInvoiceTotal]);
  const previewRemainingDue = useMemo(
    () => Math.max(previewInvoiceTotal - previewAmountPaid, 0),
    [previewInvoiceTotal, previewAmountPaid]
  );
  const previewFeeBase = isBlitzPay ? previewRemainingDue : previewInvoiceTotal;
  const previewPlatformFee = useMemo(
    () => (previewFeeBase * previewFeePercentage) / 100,
    [previewFeeBase, previewFeePercentage]
  );
  const amountReceive = useMemo(
    () => (previewFeeBase - previewPlatformFee).toFixed(2),
    [previewFeeBase, previewPlatformFee]
  );

  const tabs = [
    { label:"Client & Billing", icon:<Person /> },
    ...(isBlitzPay ? [{ label:"Company Profile", icon:<Business /> }] : []),
  ];

  return (
    <Dialog open={open} onClose={()=>onClose(false)} maxWidth="xl" fullWidth
      PaperProps={{ sx:{ height:"95vh", maxHeight:"95vh", display:"flex", flexDirection:"column" } }}>

      <DialogTitle sx={{ borderBottom:"2px solid #e0e0e0", pb:2, flexShrink:0 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor:typeConfig.color, width:40, height:40 }}><Receipt /></Avatar>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={700}>Edit Invoice</Typography>
            <Typography variant="caption" color="textSecondary">{typeConfig.description}</Typography>
          </Box>
          <Chip label={typeConfig.label} sx={{ bgcolor:typeConfig.color, color:'white', fontWeight:700, fontSize:'0.875rem', px:2 }} />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p:0, flex:1, overflow:"hidden", display:"flex" }}>
        <Box sx={{ display:"flex", width:"100%", height:"100%" }}>

          {/* LEFT */}
          <Box sx={{ flex:"0 0 540px", borderRight:"2px solid #e0e0e0", display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
            <Box sx={{ borderBottom:"2px solid #e0e0e0", bgcolor:"#fafafa", flexShrink:0 }}>
              <Tabs value={activeTab} onChange={(_,v)=>setActiveTab(v)} variant="fullWidth"
                sx={{ "& .MuiTab-root":{ fontWeight:700, textTransform:"none", fontSize:"0.88rem", py:1.5, minHeight:46 } }}>
                {tabs.map((t,i) => <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />)}
              </Tabs>
            </Box>

            <Box sx={{ flex:1, overflowY:"auto", overflowX:"hidden" }}>
              <CoreInvoiceSection state={coreState} setState={setCoreState} allowedStatuses={allowedStatuses}
                isValidStatus={isValidStatus} statusHelper={statusHelper} typeConfig={typeConfig} />

              {repaymentState.isValidating && (
                <Box sx={{ p:2, textAlign:'center', bgcolor:'#f0fff4', borderBottom:"1px solid #10b981" }}>
                  <CircularProgress size={18} />
                  <Typography variant="caption" sx={{ ml:1 }}>Checking repayment eligibility…</Typography>
                </Box>
              )}
              {!repaymentState.isValidating && repaymentState.isEligible && (
                <RepaymentSection repaymentState={repaymentState} setRepaymentState={setRepaymentState}
                  amountDue={coreState.amountDue} isChangingToPaid={isChangingToPaid} />
              )}

              <Box sx={{ p:2.5 }}>
                {activeTab === 0 && (
                  <ClientBillingTab
                    invoiceType={invoiceType}
                    isBlitzPay={isBlitzPay}
                    billedTo={billedTo} setBilledTo={setBilledTo}
                    enterpriseClient={enterpriseClient} setEnterpriseClient={setEnterpriseClient}
                    profileLoading={profileLoading} loadedProfile={loadedProfile}
                    onEditProfile={() => setActiveTab(1)}
                    clientInfo={clientInfo} setClientInfo={setClientInfo}
                    billingAddress={billingAddress} setBillingAddress={setBillingAddress}
                    projectBank={projectBank} setProjectBank={setProjectBank}
                    typeConfig={typeConfig}
                  />
                )}
                {activeTab === 1 && isBlitzPay && (
                  <BlitzPayProfileTab
                    loadedProfile={loadedProfile} profileLoading={profileLoading}
                    onRefresh={refetchProfile}
                    billedTo={billedTo} setBilledTo={setBilledTo}
                    billedFrom={billedFrom} setBilledFrom={setBilledFrom}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* RIGHT — preview */}
          <Box sx={{ flex:1, p:3, overflowY:"auto", bgcolor:"#f8f9fa" }}>
            <InvoicePreview
              coreState={coreState}
              billedTo={billedTo}
              billedFrom={billedFrom}
              enterpriseClient={enterpriseClient}
              clientInfo={clientInfo}
              billingAddress={billingAddress}
              projectBank={projectBank}
              previewInvoiceTotal={previewInvoiceTotal}
              previewAmountPaid={previewAmountPaid}
              previewRemainingDue={previewRemainingDue}
              previewPlatformFee={previewPlatformFee}
              amountReceive={amountReceive}
              typeConfig={typeConfig}
              isBlitzPay={isBlitzPay}
              repaymentState={repaymentState}
              isChangingToPaid={isChangingToPaid}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p:2.5, bgcolor:"#fafafa", borderTop:"2px solid #e0e0e0", flexShrink:0 }}>
        <Button disabled={isLoading} onClick={()=>onClose(false)} variant="outlined" size="large" sx={{ borderRadius:2, px:4, fontWeight:600 }}>Cancel</Button>
        <Button disabled={isLoading||!isValidStatus} onClick={handleSubmit} variant="contained" size="large"
          sx={{ borderRadius:2, px:4, fontWeight:700, bgcolor:typeConfig.color, '&:hover':{ bgcolor:typeConfig.color, filter:'brightness(0.9)' } }}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Save />}>
          {isLoading ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ====================================================== CORE SECTION === */

const CoreInvoiceSection = ({ state, setState, allowedStatuses, isValidStatus, statusHelper, typeConfig }) => {
  const u = (f,v) => setState(p=>({...p,[f]:v}));
  return (
    <Box sx={{ p:2.5, bgcolor:"#fff", borderBottom:"2px solid #e0e0e0" }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Avatar sx={{ bgcolor:typeConfig.color, width:32, height:32 }}><Assignment fontSize="small" /></Avatar>
        <Typography variant="subtitle1" fontWeight={700}>Core Invoice Details</Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12}><TextField size="small" label="PO Number" fullWidth value={state.poNumber} onChange={e=>u('poNumber',e.target.value)} InputProps={{ startAdornment:<Receipt fontSize="small" sx={{ mr:1, color:typeConfig.color }} /> }} /></Grid>
        <Grid item xs={6}><TextField size="small" label="Amount Due" fullWidth type="number" value={state.amountDue} onChange={e=>u('amountDue',e.target.value)} InputProps={{ startAdornment:<AttachMoney fontSize="small" sx={{ color:"success.main" }} /> }} /></Grid>
        <Grid item xs={6}><TextField size="small" label="Fee %" fullWidth type="number" inputProps={{ step: '0.01' }} value={state.feePercentage} onChange={e=>u('feePercentage',e.target.value)} /></Grid>
        <Grid item xs={12}>
          <TextField size="small" label="Status" fullWidth select value={state.status} onChange={e=>u('status',e.target.value)} error={!isValidStatus} helperText={statusHelper} FormHelperTextProps={{ sx:{ color:!isValidStatus?'error.main':'text.secondary', fontWeight:500 } }}>
            {invoiceStatuses.map(st => { const ok=allowedStatuses.includes(st); return <MenuItem key={st} value={st} disabled={!ok} sx={{ opacity:ok?1:0.4, fontWeight:600 }}>{st.charAt(0).toUpperCase()+st.slice(1)}{!ok&&' (Not Allowed)'}</MenuItem>; })}
          </TextField>
        </Grid>
        <Grid item xs={6}><TextField size="small" label="Payment Term (days)" fullWidth type="number" value={state.paymentTerm} onChange={e=>u('paymentTerm',e.target.value)} /></Grid>
        <Grid item xs={6}><TextField size="small" label="Funded By" fullWidth select value={state.fundedBy} onChange={e=>u('fundedBy',e.target.value)}><MenuItem value="">None</MenuItem>{FUNDING_SOURCES.map(f=><MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</TextField></Grid>
        <Grid item xs={6}><TextField size="small" label="Date Paid" fullWidth type="datetime-local" value={state.datePaid?dateToDatetimeLocal(state.datePaid):""} onChange={e=>u('datePaid',e.target.value?new Date(e.target.value):null)} InputLabelProps={{ shrink:true }} /></Grid>
        <Grid item xs={6}><TextField size="small" label="Funded Date" fullWidth type="datetime-local" value={state.fundedDate?dateToDatetimeLocal(state.fundedDate):""} onChange={e=>u('fundedDate',e.target.value?new Date(e.target.value):null)} InputLabelProps={{ shrink:true }} /></Grid>
      </Grid>
      {!isValidStatus && <Alert severity="error" icon={<Warning />} sx={{ mt:2 }}><Typography variant="body2" fontWeight={700}>Invalid Status Transition</Typography><Typography variant="caption">{statusHelper}</Typography></Alert>}
    </Box>
  );
};

/* =================================================== REPAYMENT SECTION === */

const RepaymentSection = ({ repaymentState, setRepaymentState, amountDue, isChangingToPaid }) => {
  const u = (f,v) => setRepaymentState(p=>({...p,[f]:v}));
  return (
    <Box sx={{ p:2.5, bgcolor:"#f0fff4", borderBottom:"2px solid #10b981" }}>
      <Alert severity="success" icon={<CheckCircle />} sx={{ mb:2 }}>
        <AlertTitle fontWeight={700}>BlitzPay Repayment Eligible</AlertTitle>
        <Typography variant="body2" fontWeight={600}>Will restore <strong>${parseLocalFloat(amountDue).toFixed(2)}</strong> to <strong>{repaymentState.validation.invoice.spending_source_name}</strong></Typography>
      </Alert>
      <Stack spacing={2}>
        <TextField size="small" label="Transaction ID *" fullWidth value={repaymentState.externalTransactionId} onChange={e=>u('externalTransactionId',e.target.value)} helperText="External payment transaction reference" required={isChangingToPaid} />
        <TextField size="small" label="Payment Method *" fullWidth select value={repaymentState.paymentMethod} onChange={e=>u('paymentMethod',e.target.value)} required={isChangingToPaid}>
          {PAYMENT_METHODS.map(m=><MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Notes (Optional)" fullWidth multiline rows={2} value={repaymentState.notes} onChange={e=>u('notes',e.target.value)} />
      </Stack>
      {isChangingToPaid && <Alert severity="warning" sx={{ mt:2 }}><Typography variant="body2" fontWeight={700}>Auto-Repayment Active</Typography><Typography variant="caption">Changing to 'paid' will automatically process this repayment.</Typography></Alert>}
    </Box>
  );
};

/* ================================================= CLIENT & BILLING TAB === */

const ClientBillingTab = ({
  invoiceType,
  isBlitzPay,
  billedTo, setBilledTo, enterpriseClient, setEnterpriseClient,
  profileLoading, loadedProfile, onEditProfile,
  clientInfo, setClientInfo, billingAddress, setBillingAddress, projectBank, setProjectBank, typeConfig,
}) => {
  const uBT = (f,v) => setBilledTo(p=>({...p,[f]:v}));
  const uEC = (f,v) => setEnterpriseClient(p=>({...p,[f]:v}));
  const uC  = (f,v) => setClientInfo(p=>({...p,[f]:v}));
  const uA  = (f,v) => setBillingAddress(p=>({...p,[f]:v}));
  const uPB = (f,v) => setProjectBank(p=>({...p,[f]:v}));
  const isProjectBankEmpty = Object.values(projectBank || {}).every(value => !value);
  const clearProjectBank = () => setProjectBank(EMPTY_PROJECT_BANK_OVERRIDE);

  if (isBlitzPay) {
    return (
      <Stack spacing={2.5}>

        {/* ── Billed To — User's Company ─────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius:2, border:"2px solid #667eea" }}>
          <CardContent sx={{ p:2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
              <Avatar sx={{ bgcolor:"#667eea", width:30, height:30 }}><Business fontSize="small" /></Avatar>
              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700}>Billed To — Recipient Company</Typography>
                <Typography variant="caption" color="textSecondary">
                  {profileLoading
                    ? "Loading from company profile…"
                    : loadedProfile
                      ? `Auto-populated from ${loadedProfile.invoice_sender_name || loadedProfile.company_name || "company profile"}`
                      : "Seeded from user account — save to create profile"}
                </Typography>
              </Box>
              {profileLoading && <CircularProgress size={16} />}
              <Tooltip title="Edit company profile universally">
                <IconButton size="small" onClick={onEditProfile} sx={{ color:"#667eea" }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Alert severity="info" sx={{ mb:1.5, py:0.5 }}>
              <Typography variant="caption">
                These fields auto-populate from the user's BlitzPay Company Profile and apply to all their invoices.
                Use the <strong>Company Profile</strong> tab to update them universally.
              </Typography>
            </Alert>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Company Name" fullWidth value={billedTo.companyName} onChange={e=>uBT('companyName',e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Contact Name" fullWidth value={billedTo.contactName} onChange={e=>uBT('contactName',e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Email" fullWidth type="email" value={billedTo.email} onChange={e=>uBT('email',e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Phone" fullWidth value={billedTo.phone} onChange={e=>uBT('phone',e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" label="Address Line 1" fullWidth value={billedTo.address1} onChange={e=>uBT('address1',e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" label="Address Line 2" fullWidth value={billedTo.address2} onChange={e=>uBT('address2',e.target.value)} />
              </Grid>
              <Grid item xs={6}><TextField size="small" label="City"  fullWidth value={billedTo.city}  onChange={e=>uBT('city',e.target.value)} /></Grid>
              <Grid item xs={3}><TextField size="small" label="State" fullWidth value={billedTo.state} onChange={e=>uBT('state',e.target.value)} /></Grid>
              <Grid item xs={3}><TextField size="small" label="Zip"   fullWidth value={billedTo.zip}   onChange={e=>uBT('zip',e.target.value)} /></Grid>
              <Grid item xs={6}><TextField size="small" label="Country"      fullWidth value={billedTo.country} onChange={e=>uBT('country',e.target.value)} /></Grid>
              <Grid item xs={6}><TextField size="small" label="Tax ID / EIN" fullWidth value={billedTo.taxId} onChange={e=>uBT('taxId',e.target.value)} placeholder="XX-XXXXXXX" /></Grid>
              <Grid item xs={12}>
                <TextField size="small" label="Amount Paid (if partial)" fullWidth type="number" value={billedTo.amountPaid} onChange={e=>uBT('amountPaid',e.target.value)}
                  InputProps={{ startAdornment:<AttachMoney fontSize="small" sx={{ color:"success.main" }} /> }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── Original Enterprise Client ──────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius:2, bgcolor:"#fafafa" }}>
          <CardContent sx={{ p:2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
              <Avatar sx={{ bgcolor:"#94a3b8", width:28, height:28 }}><Person fontSize="small" /></Avatar>
              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Original Enterprise Client</Typography>
                <Typography variant="caption" color="textSecondary">Who owes the underlying invoice (stored as client_name / client_email)</Typography>
              </Box>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Enterprise Client Name" fullWidth value={enterpriseClient.name} onChange={e=>uEC('name',e.target.value)} placeholder="e.g., Atlantic Records" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Enterprise Client Email" fullWidth type="email" value={enterpriseClient.email} onChange={e=>uEC('email',e.target.value)} placeholder="ap@atlantic.com" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  // Non-BlitzPay
  return (
    <Stack spacing={2.5}>
      <Card variant="outlined" sx={{ borderRadius:2 }}>
        <CardContent sx={{ p:2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
            <Avatar sx={{ bgcolor:"#f59e0b", width:30, height:30 }}><LocationOn fontSize="small" /></Avatar>
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight={700}>{typeConfig.billingAddressLabel}</Typography>
              <Typography variant="caption" color="textSecondary">
                Primary billing entity shown on the invoice. Falls back to the legacy contact fields below if left blank.
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1.5}>
            <TextField size="small" label="Company Name"   fullWidth value={billingAddress.companyName} onChange={e=>uA('companyName',e.target.value)} />
            <TextField size="small" label="Address Line 1" fullWidth value={billingAddress.address1}    onChange={e=>uA('address1',e.target.value)} />
            <TextField size="small" label="Address Line 2" fullWidth value={billingAddress.address2}    onChange={e=>uA('address2',e.target.value)} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}><TextField size="small" label="City"    fullWidth value={billingAddress.city}    onChange={e=>uA('city',e.target.value)} /></Grid>
              <Grid item xs={6}><TextField size="small" label="State"   fullWidth value={billingAddress.state}   onChange={e=>uA('state',e.target.value)} /></Grid>
            </Grid>
            <Grid container spacing={1.5}>
              <Grid item xs={6}><TextField size="small" label="Zip"     fullWidth value={billingAddress.zip}     onChange={e=>uA('zip',e.target.value)} /></Grid>
              <Grid item xs={6}><TextField size="small" label="Country" fullWidth value={billingAddress.country} onChange={e=>uA('country',e.target.value)} /></Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ borderRadius:2 }}>
        <CardContent sx={{ p:2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
            <Avatar sx={{ bgcolor:"#3b82f6", width:30, height:30 }}><Person fontSize="small" /></Avatar>
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight={700}>Invoice Contact & Fallback</Typography>
              <Typography variant="caption" color="textSecondary">
                Optional legacy contact fields for delivery and older invoices when no bill-to company/address is available.
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1.5}>
            <TextField size="small" label="Contact Name" fullWidth value={clientInfo.billedToName} onChange={e=>uC('billedToName',e.target.value)} />
            <TextField size="small" label="Contact Email" fullWidth type="email" value={clientInfo.billedToEmail} onChange={e=>uC('billedToEmail',e.target.value)} />
            <TextField size="small" label="Issuing Company" fullWidth value={clientInfo.issuingCompany} onChange={e=>uC('issuingCompany',e.target.value)} helperText="Company issuing this invoice" />
            <TextField
              size="small"
              label="Amount Paid"
              fullWidth
              type="number"
              value={clientInfo.amountPaid}
              onChange={e=>uC('amountPaid',e.target.value)}
              InputProps={{ startAdornment:<AttachMoney fontSize="small" sx={{ color:"success.main" }} /> }}
              helperText="If partially paid, enter amount"
            />
          </Stack>
        </CardContent>
      </Card>
      {invoiceType === 'project' && (
        <Card variant="outlined" sx={{ borderRadius:2 }}>
          <CardContent sx={{ p:2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
              <Avatar sx={{ bgcolor:"#ef4444", width:30, height:30 }}><AccountBalance fontSize="small" /></Avatar>
              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700}>Bank Override</Typography>
                <Typography variant="caption" color="textSecondary">
                  Leave blank to use the default project invoice banking information.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={clearProjectBank}
                disabled={isProjectBankEmpty}
                sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
              >
                Clear All
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              <TextField size="small" label="Bank Name" fullWidth value={projectBank.bankName} onChange={e=>uPB('bankName',e.target.value)} />
              <TextField size="small" label="Account Holder" fullWidth value={projectBank.accountHolder} onChange={e=>uPB('accountHolder',e.target.value)} />
              <TextField size="small" label="Account Number" fullWidth value={projectBank.accountNumber} onChange={e=>uPB('accountNumber',e.target.value)} />
              <TextField size="small" label="Routing Number" fullWidth value={projectBank.routingNumber} onChange={e=>uPB('routingNumber',e.target.value)} />
              <TextField size="small" label="SWIFT Code" fullWidth value={projectBank.swift} onChange={e=>uPB('swift',e.target.value)} />
              <TextField size="small" label="Beneficiary Address" fullWidth value={projectBank.beneficiaryAddress} onChange={e=>uPB('beneficiaryAddress',e.target.value)} />
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

/* ===================================== BLITZPAY COMPANY PROFILE TAB === */

const BlitzPayProfileTab = ({ loadedProfile, profileLoading, onRefresh, billedTo, setBilledTo, billedFrom, setBilledFrom }) => {
  const u  = (f,v) => setBilledTo(p=>({...p,[f]:v}));
  const uF = (f,v) => setBilledFrom(p=>({...p,[f]:v}));

  return (
    <Stack spacing={2.5}>

      {/* Header card */}
      <Card variant="outlined" sx={{ bgcolor:"#f0f4ff", border:"2px solid #667eea" }}>
        <CardContent sx={{ p:2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor:"#667eea" }}><Business /></Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>BlitzPay Company Profile</Typography>
                <Typography variant="caption" color="textSecondary">
                  {profileLoading
                    ? "Loading…"
                    : loadedProfile
                      ? `Profile: ${loadedProfile.invoice_sender_name || loadedProfile.company_name || "—"}`
                      : "No profile yet — will be created on save"}
                </Typography>
              </Box>
            </Box>
            {!profileLoading && onRefresh && (
              <Tooltip title="Refresh profile"><IconButton size="small" onClick={onRefresh}><Refresh /></IconButton></Tooltip>
            )}
          </Stack>
          {profileLoading && <Box textAlign="center" pt={1}><CircularProgress size={20} /></Box>}
          {!profileLoading && (
            <Alert severity={loadedProfile ? "success" : "info"} icon={loadedProfile ? <CheckCircle /> : <Info />} sx={{ mt:1.5 }}>
              <Typography variant="caption">
                {loadedProfile
                  ? "Changes here update the universal profile and auto-populate all future BlitzPay invoices for this user."
                  : "Saving this invoice will create a new profile for this user. It will auto-populate all future BlitzPay invoices."}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── Invoice Sender / Top-Right ─────────────────────────────────── */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor:"#fff0f0", minHeight:44, '& .MuiAccordionSummary-content':{ my:0.75 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor:"#ef4444", width:26, height:26 }}><Storefront sx={{ fontSize:15 }} /></Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>Invoice Sender — Top-Right of Invoice</Typography>
              <Typography variant="caption" color="textSecondary">
                The issuing entity / factor shown in the invoice header. Defaults to the current BlitzPay remittance profile.
              </Typography>
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt:2 }}>
          <Alert severity="info" sx={{ mb:2, py:0.5 }}>
            <Typography variant="caption">
              Controls the <strong>top-right corner</strong> of the invoice — who the invoice is <em>from</em>.
              Override here when the factor is registered under a different name in the client's AP system.
              This is saved per-invoice only and does not affect the user's company profile.
            </Typography>
          </Alert>
          <Stack spacing={1.5}>
            <TextField
              size="small" label="Legal Company Name" fullWidth
              value={billedFrom.companyName}
              onChange={e=>uF('companyName',e.target.value)}
              helperText={`Default: "${BLITZ_DEFAULTS.company_name}"`}
            />
            <TextField
              size="small" label="Invoice Display Name" fullWidth
              value={billedFrom.senderDisplayName}
              onChange={e=>uF('senderDisplayName',e.target.value)}
              placeholder={billedFrom.companyName || BLITZ_DEFAULTS.company_name}
              helperText='Name shown on invoice header when different from legal name (e.g. "Blitz Co" as registered in Sony AP). Leave blank to use Legal Company Name.'
              InputProps={{ startAdornment:<Storefront fontSize="small" sx={{ mr:1, color:"#ef4444" }} /> }}
            />
            <TextField
              size="small" label="Email" fullWidth
              value={billedFrom.email}
              onChange={e=>uF('email',e.target.value)}
              placeholder={BLITZ_DEFAULTS.email}
            />
            <TextField size="small" label="Phone" fullWidth value={billedFrom.phone} onChange={e=>uF('phone',e.target.value)} />
            <TextField size="small" label="Tax ID / EIN" fullWidth value={billedFrom.taxId} onChange={e=>uF('taxId',e.target.value)} placeholder="XX-XXXXXXX" />
            <TextField
              size="small" label="Logo URL" fullWidth
              value={billedFrom.logoUrl}
              onChange={e=>uF('logoUrl',e.target.value)}
              helperText="URL to sender logo — shown in invoice header"
            />
            <Divider />
            <TextField size="small" label="Address Line 1" fullWidth value={billedFrom.address1} onChange={e=>uF('address1',e.target.value)} placeholder={BLITZ_DEFAULTS.address_line1} />
            <TextField size="small" label="Address Line 2" fullWidth value={billedFrom.address2} onChange={e=>uF('address2',e.target.value)} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}><TextField size="small" label="City"  fullWidth value={billedFrom.city}  onChange={e=>uF('city',e.target.value)}  placeholder={BLITZ_DEFAULTS.city} /></Grid>
              <Grid item xs={6}><TextField size="small" label="State" fullWidth value={billedFrom.state} onChange={e=>uF('state',e.target.value)} placeholder={BLITZ_DEFAULTS.state} /></Grid>
            </Grid>
            <Grid container spacing={1.5}>
              <Grid item xs={6}><TextField size="small" label="Zip"     fullWidth value={billedFrom.zip}     onChange={e=>uF('zip',e.target.value)}     placeholder={BLITZ_DEFAULTS.zip_code} /></Grid>
              <Grid item xs={6}><TextField size="small" label="Country" fullWidth value={billedFrom.country} onChange={e=>uF('country',e.target.value)} /></Grid>
            </Grid>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Company Info (Recipient / Billed To) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor:"#f0f4ff", minHeight:44, '& .MuiAccordionSummary-content':{ my:0.75 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor:"#667eea", width:26, height:26 }}><Business sx={{ fontSize:15 }} /></Avatar>
            <Typography variant="subtitle2" fontWeight={700}>Recipient Company Info (Billed To)</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt:2 }}>
          <Stack spacing={1.5}>
            <TextField
              size="small" label="Company Name *" fullWidth
              value={billedTo.companyName}
              onChange={e=>u('companyName',e.target.value)}
              helperText="Legal / tax entity name stored on the profile"
            />
            <TextField
              size="small" label="Invoice Sender Name" fullWidth
              value={billedTo.senderDisplayName}
              onChange={e=>u('senderDisplayName',e.target.value)}
              placeholder={billedTo.companyName || "Defaults to Company Name if blank"}
              helperText='Name shown on the invoice header — use when the vendor AP system requires a specific registered name (e.g. "Blitz Co", "Alex Blitz Company"). Leave blank to use Company Name.'
              InputProps={{ startAdornment:<Storefront fontSize="small" sx={{ mr:1, color:"#667eea" }} /> }}
            />
            <TextField size="small" label="Contact Name" fullWidth value={billedTo.contactName} onChange={e=>u('contactName',e.target.value)} />
            <TextField size="small" label="Email *" fullWidth type="email" value={billedTo.email} onChange={e=>u('email',e.target.value)} />
            <TextField size="small" label="Phone" fullWidth value={billedTo.phone} onChange={e=>u('phone',e.target.value)} />
            <TextField size="small" label="Tax ID / EIN" fullWidth value={billedTo.taxId} onChange={e=>u('taxId',e.target.value)} placeholder="XX-XXXXXXX" />
            <TextField size="small" label="Logo URL" fullWidth value={billedTo.logoUrl} onChange={e=>u('logoUrl',e.target.value)} helperText="URL to company logo for invoice branding" />
            <Divider />
            <TextField size="small" label="Address Line 1" fullWidth value={billedTo.address1} onChange={e=>u('address1',e.target.value)} />
            <TextField size="small" label="Address Line 2" fullWidth value={billedTo.address2} onChange={e=>u('address2',e.target.value)} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}><TextField size="small" label="City"  fullWidth value={billedTo.city}  onChange={e=>u('city',e.target.value)} /></Grid>
              <Grid item xs={6}><TextField size="small" label="State" fullWidth value={billedTo.state} onChange={e=>u('state',e.target.value)} /></Grid>
            </Grid>
            <Grid container spacing={1.5}>
              <Grid item xs={6}><TextField size="small" label="Zip"     fullWidth value={billedTo.zip}     onChange={e=>u('zip',e.target.value)} /></Grid>
              <Grid item xs={6}><TextField size="small" label="Country" fullWidth value={billedTo.country} onChange={e=>u('country',e.target.value)} /></Grid>
            </Grid>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Vendor & ERP */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor:"#fff7ed", minHeight:44, '& .MuiAccordionSummary-content':{ my:0.75 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor:"#f59e0b", width:26, height:26 }}><Assignment sx={{ fontSize:15 }} /></Avatar>
            <Typography variant="subtitle2" fontWeight={700}>Vendor & ERP Details</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt:2 }}>
          <Stack spacing={1.5}>
            <TextField size="small" label="Primary Vendor ID" fullWidth value={billedTo.vendorId}  onChange={e=>u('vendorId',e.target.value)} helperText="Your vendor ID in client's accounting system" />
            <TextField size="small" label="ERP System Name"   fullWidth value={billedTo.erpSystem} onChange={e=>u('erpSystem',e.target.value)} placeholder="SAP, Oracle, QuickBooks…" />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Bank Details */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor:"#f0fdf4", minHeight:44, '& .MuiAccordionSummary-content':{ my:0.75 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor:"#10b981", width:26, height:26 }}><AccountBalance sx={{ fontSize:15 }} /></Avatar>
            <Typography variant="subtitle2" fontWeight={700}>Bank Account Details</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt:2 }}>
          <Stack spacing={1.5}>
            <TextField size="small" label="Account Holder Name"     fullWidth value={billedTo.bankAccountHolder} onChange={e=>u('bankAccountHolder',e.target.value)} helperText="Individual name on the account" />
            <TextField size="small" label="Account Name (Business)" fullWidth value={billedTo.bankAccountName}   onChange={e=>u('bankAccountName',e.target.value)} helperText="Business/entity name on the account" />
            <TextField size="small" label="Bank Name"               fullWidth value={billedTo.bankName}          onChange={e=>u('bankName',e.target.value)} />
            <TextField size="small" label="Bank Address"            fullWidth value={billedTo.bankAddress}       onChange={e=>u('bankAddress',e.target.value)} />
            <TextField size="small" label="Account Number"          fullWidth value={billedTo.accountNumber}     onChange={e=>u('accountNumber',e.target.value)} type="password" />
            <TextField size="small" label="Routing / ABA / IBAN"   fullWidth value={billedTo.routingNumber}     onChange={e=>u('routingNumber',e.target.value)} />
            <TextField size="small" label="SWIFT Code"              fullWidth value={billedTo.swift}             onChange={e=>u('swift',e.target.value)} />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Terms */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor:"#faf5ff", minHeight:44, '& .MuiAccordionSummary-content':{ my:0.75 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor:"#8b5cf6", width:26, height:26 }}><Description sx={{ fontSize:15 }} /></Avatar>
            <Typography variant="subtitle2" fontWeight={700}>Payment Terms & Notes</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt:2 }}>
          <Stack spacing={1.5}>
            <TextField size="small" label="Payment Instructions" fullWidth multiline rows={3} value={billedTo.paymentInstructions} onChange={e=>u('paymentInstructions',e.target.value)} />
            <TextField size="small" label="Terms and Conditions" fullWidth multiline rows={3} value={billedTo.termsAndConditions}  onChange={e=>u('termsAndConditions',e.target.value)} />
            <TextField size="small" label="Invoice Notes"        fullWidth multiline rows={2} value={billedTo.notes}               onChange={e=>u('notes',e.target.value)} />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

/* ======================================================= PREVIEW PANEL === */

const InvoicePreview = ({
  coreState, billedTo, billedFrom, enterpriseClient,
  clientInfo, billingAddress, projectBank,
  previewInvoiceTotal, previewAmountPaid, previewRemainingDue, previewPlatformFee,
  amountReceive, typeConfig, isBlitzPay, repaymentState, isChangingToPaid,
}) => {
  const displayStatus = coreState.status ? coreState.status.charAt(0).toUpperCase()+coreState.status.slice(1) : "—";
  const billingAddressPreview = [
    billingAddress.address1,
    billingAddress.address2,
    [billingAddress.city, billingAddress.state].filter(Boolean).join(", "),
    billingAddress.zip,
    billingAddress.country,
  ].filter(Boolean).join(", ");
  const hasBillingAddressOverride = Boolean(
    billingAddress.companyName || billingAddressPreview
  );

  // Top-right sender name
  const senderDisplayName = isBlitzPay
    ? (billedFrom.senderDisplayName || billedFrom.companyName || BLITZ_DEFAULTS.company_name)
    : (clientInfo.issuingCompany || "Blitz Systems Corp");

  const senderLegalName = isBlitzPay ? (billedFrom.companyName || BLITZ_DEFAULTS.company_name) : null;
  const showSenderSubline = isBlitzPay && billedFrom.senderDisplayName && billedFrom.companyName && billedFrom.senderDisplayName !== billedFrom.companyName;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Typography variant="h5" fontWeight={700}>Live Preview</Typography>
        <Chip label={typeConfig.label} sx={{ bgcolor:typeConfig.color, color:'white', fontWeight:700, px:2 }} />
      </Stack>
      <Paper elevation={6} sx={{ p:4, background:"#fff", borderRadius:3, border:`3px solid ${typeConfig.color}` }}>

        {/* Header */}
        <Grid container spacing={3} sx={{ mb:3 }}>
          <Grid item xs={6}>
            <Typography variant="h3" sx={{ fontWeight:900, color:typeConfig.color, mb:1, letterSpacing:'-0.5px' }}>INVOICE</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign:"right" }}>
            {/* ── TOP-RIGHT: Sender / Billed From ── */}
            <Box sx={{ mb:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:'#1e293b' }}>{senderDisplayName}</Typography>
              {showSenderSubline && (
                <Typography variant="caption" display="block" color="textSecondary" sx={{ fontStyle:'italic' }}>
                  ({senderLegalName})
                </Typography>
              )}
              {isBlitzPay && (
                <>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {billedFrom.address1 || BLITZ_DEFAULTS.address_line1}
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {billedFrom.city || BLITZ_DEFAULTS.city}, {billedFrom.state || BLITZ_DEFAULTS.state} {billedFrom.zip || BLITZ_DEFAULTS.zip_code}
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {billedFrom.email || BLITZ_DEFAULTS.email}
                  </Typography>
                  {billedFrom.phone && (
                    <Typography variant="caption" display="block" color="textSecondary">{billedFrom.phone}</Typography>
                  )}
                  {billedFrom.taxId && (
                    <Typography variant="caption" display="block" color="textSecondary">Tax ID: {billedFrom.taxId}</Typography>
                  )}
                </>
              )}
            </Box>
            <Card sx={{ p:2, display:"inline-block", bgcolor:'#f8f9fa', border:`3px solid ${typeConfig.color}`, borderRadius:2, minWidth:150 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display:"block", mb:1, fontWeight:600 }}>STATUS</Typography>
              <Chip label={displayStatus} color={coreState.status==='paid'?'success':coreState.status==='funded'?'secondary':'default'} sx={{ fontWeight:800, fontSize:'0.875rem' }} />
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my:2.5, borderWidth:2 }} />

        {/* Details row */}
        <Grid container spacing={3} sx={{ mb:3 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p:2.5, borderRadius:2, bgcolor:"#f8fafc", height:'100%' }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:1.5 }}>
                <Avatar sx={{ width:28, height:28, bgcolor:typeConfig.color }}><Receipt sx={{ fontSize:15 }} /></Avatar>
                <Typography variant="subtitle2" fontWeight={700}>Invoice Details</Typography>
              </Box>
              <DetailRow label="PO Number"  value={coreState.poNumber||"—"} bold />
              {coreState.externalPO && <DetailRow label="Client PO"  value={coreState.externalPO} />}
              <DetailRow label="Amount Due" value={`$${previewRemainingDue.toFixed(2)}`} bold />
              <DetailRow label="Fee"        value={`${coreState.feePercentage}%`} />
              <DetailRow label="Terms"      value={`Net ${coreState.paymentTerm} days`} />
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p:2.5, borderRadius:2, bgcolor:"#f0f4ff", border:"2px solid #667eea", height:'100%' }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:1.5 }}>
                <Avatar sx={{ width:28, height:28, bgcolor:"#667eea" }}><Business sx={{ fontSize:15 }} /></Avatar>
                <Typography variant="subtitle2" fontWeight={700}>Billed To</Typography>
              </Box>
              {isBlitzPay ? (
                <>
                  <DetailRow label="Company"  value={billedTo.companyName || billedTo.senderDisplayName || "—"} bold />
                  {billedTo.contactName && <DetailRow label="Contact" value={billedTo.contactName} />}
                  <DetailRow label="Email"   value={billedTo.email||"—"} />
                  {billedTo.phone && <DetailRow label="Phone" value={billedTo.phone} />}
                  {billedTo.address1 && (
                    <DetailRow label="Address" value={`${billedTo.address1}${billedTo.city?`, ${billedTo.city}, ${billedTo.state} ${billedTo.zip}`:''}`} />
                  )}
                  {billedTo.taxId && <DetailRow label="Tax ID" value={billedTo.taxId} />}
                </>
              ) : (
                <>
                  {hasBillingAddressOverride ? (
                    <>
                      <DetailRow label="Company" value={billingAddress.companyName || clientInfo.billedToName || "—"} bold />
                      {billingAddressPreview && <DetailRow label="Address" value={billingAddressPreview} />}
                    </>
                  ) : (
                    <>
                      <DetailRow label="Name"  value={clientInfo.billedToName||"—"} bold />
                      <DetailRow label="Email" value={clientInfo.billedToEmail||"—"} />
                    </>
                  )}
                </>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Enterprise client (BlitzPay only) */}
        {isBlitzPay && (enterpriseClient.name || enterpriseClient.email) && (
          <Card variant="outlined" sx={{ p:2, mb:3, borderRadius:2, bgcolor:"#f8f9fa" }}>
            <Typography variant="caption" color="textSecondary" fontWeight={700} display="block" mb={0.5}>ORIGINAL ENTERPRISE CLIENT</Typography>
            <Stack direction="row" spacing={3}>
              {enterpriseClient.name  && <DetailRow label="Name"  value={enterpriseClient.name} />}
              {enterpriseClient.email && <DetailRow label="Email" value={enterpriseClient.email} />}
            </Stack>
          </Card>
        )}

        {!isBlitzPay && typeConfig.label === 'Project' && (
          <Card variant="outlined" sx={{ p:2, mb:3, borderRadius:2, bgcolor:"#fffaf9", border:"1px solid #ead7dc" }}>
            <Typography variant="caption" color="textSecondary" fontWeight={700} display="block" mb={1}>BANKING INFORMATION</Typography>
            <DetailRow label="Bank" value={projectBank.bankName || "Default project bank info"} />
            <DetailRow label="Account Holder" value={projectBank.accountHolder || "Blitz Systems Corp"} />
            {(projectBank.accountNumber || projectBank.routingNumber) && (
              <DetailRow label="Account / Routing" value={`${projectBank.accountNumber || "—"} / ${projectBank.routingNumber || "—"}`} />
            )}
            {projectBank.swift && <DetailRow label="SWIFT" value={projectBank.swift} />}
            {projectBank.beneficiaryAddress && <DetailRow label="Beneficiary Address" value={projectBank.beneficiaryAddress} />}
          </Card>
        )}

        {/* Bank details (BlitzPay) */}
        {isBlitzPay && billedTo.bankName && (
          <Card variant="outlined" sx={{ p:2.5, mb:3, borderRadius:2, bgcolor:"#f0fdf4", border:"1px solid #10b981" }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:1 }}>
              <Avatar sx={{ bgcolor:"#10b981", width:24, height:24 }}><AccountBalance sx={{ fontSize:13 }} /></Avatar>
              <Typography variant="caption" fontWeight={700} color="#10b981">PAYMENT TO</Typography>
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}><DetailRow label="Bank" value={billedTo.bankName} /></Grid>
              {billedTo.routingNumber && <Grid item xs={12} md={6}><DetailRow label="Routing" value={billedTo.routingNumber} /></Grid>}
              {billedTo.bankAccountHolder && <Grid item xs={12} md={6}><DetailRow label="Holder" value={billedTo.bankAccountHolder} /></Grid>}
            </Grid>
          </Card>
        )}

        {/* Repayment */}
        {repaymentState.isEligible && repaymentState.validation && (
          <Alert severity="success" sx={{ mb:3, border:"2px solid #10b981" }}>
            <AlertTitle fontWeight={700}>Repayment Will Restore Spending Power</AlertTitle>
            <Grid container spacing={1.5} mt={0.25}>
              <Grid item xs={12} md={6}><DetailRow label="Source"  value={repaymentState.validation.invoice.spending_source_name} bold /></Grid>
              <Grid item xs={12} md={6}><DetailRow label="Restore" value={`$${parseLocalFloat(coreState.amountDue).toFixed(2)}`} bold /></Grid>
              {repaymentState.externalTransactionId && <Grid item xs={12} md={6}><DetailRow label="Tx ID"   value={repaymentState.externalTransactionId} /></Grid>}
              {repaymentState.paymentMethod          && <Grid item xs={12} md={6}><DetailRow label="Method" value={PAYMENT_METHODS.find(m=>m.value===repaymentState.paymentMethod)?.label||repaymentState.paymentMethod} /></Grid>}
            </Grid>
          </Alert>
        )}

        {/* Totals */}
        <Divider sx={{ my:3, borderWidth:2 }} />
        <Grid container justifyContent="flex-end">
          <Grid item xs={12} sm={6}>
            <Card sx={{ p:3, bgcolor:typeConfig.color, color:'white', borderRadius:3 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>Total Invoice</Typography><Typography fontWeight={600}>${previewInvoiceTotal.toFixed(2)}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>Amount Paid</Typography><Typography fontWeight={600}>${previewAmountPaid.toFixed(2)}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>Amount Due</Typography><Typography fontWeight={600}>${previewRemainingDue.toFixed(2)}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>Platform Fee ({coreState.feePercentage}%)</Typography><Typography fontWeight={600}>-${previewPlatformFee.toFixed(2)}</Typography></Stack>
                <Divider sx={{ borderColor:'rgba(255,255,255,0.3)' }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="subtitle1" fontWeight={900}>Amount to Receive</Typography><Typography variant="h5" fontWeight={900}>${amountReceive}</Typography></Stack>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Terms */}
        {isBlitzPay && (billedTo.termsAndConditions || billedTo.paymentInstructions || billedTo.notes) && (
          <Box mt={3} pt={3} borderTop="2px solid #e0e0e0">
            {billedTo.paymentInstructions && <Box mb={2}><Typography variant="subtitle2" fontWeight={700} color="textSecondary" mb={0.5}>PAYMENT INSTRUCTIONS</Typography><Typography variant="body2" color="textSecondary">{billedTo.paymentInstructions}</Typography></Box>}
            {billedTo.termsAndConditions  && <Box mb={2}><Typography variant="subtitle2" fontWeight={700} color="textSecondary" mb={0.5}>TERMS & CONDITIONS</Typography><Typography variant="body2" color="textSecondary">{billedTo.termsAndConditions}</Typography></Box>}
            {billedTo.notes               && <Box><Typography variant="subtitle2" fontWeight={700} color="textSecondary" mb={0.5}>NOTES</Typography><Typography variant="body2" color="textSecondary">{billedTo.notes}</Typography></Box>}
          </Box>
        )}
      </Paper>
    </>
  );
};

const DetailRow = ({ label, value, bold=false }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb:0.5 }}>
    <Typography variant="body2" color="textSecondary" sx={{ fontWeight:600, minWidth:'80px', flexShrink:0 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight:bold?700:500, color:bold?"primary.main":"text.primary", textAlign:'right', flex:1, wordBreak:'break-word' }}>{value}</Typography>
  </Stack>
);

export default InvoiceDialog;
